/**
 * Krea.AI — Estudio de generación de piezas con IA brand-aware.
 *
 * Desarrollado por wearedatalab.com
 */
import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { BrandProvider } from "@/lib/brand";
import { PlatformConfigProvider } from "@/lib/platformConfig";
import { GenerationsProvider } from "@/lib/generations";
import AuthGuard from "@/lib/AuthGuard";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Chat from "@/pages/Chat";
import LibraryPage from "@/pages/LibraryPage";
import References from "@/pages/References";
import CopyStudio from "@/pages/CopyStudio";
import CommunicationStandard from "@/pages/CommunicationStandard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminModels from "@/pages/admin/AdminModels";
import AdminGeneral from "@/pages/admin/AdminGeneral";
import AdminRatings from "@/pages/admin/AdminRatings";
import Users from "@/pages/Users";
import Stats from "@/pages/Stats";
import Logs from "@/pages/Logs";
import Tutorials from "@/pages/Tutorials";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import QuotaAlertBanner from "@/components/QuotaAlertBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ConfirmProvider } from "@/components/ConfirmDialog";

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <PlatformConfigProvider>
                    <BrandProvider>
                        <GenerationsProvider>
                        <ConfirmProvider>
                            <BrowserRouter>
                    <ImpersonationBanner />
                    <QuotaAlertBanner />
                    <ErrorBoundary>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route
                            path="/"
                            element={
                                <AuthGuard>
                                    <Home />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/chats/:id"
                            element={
                                <AuthGuard>
                                    {/*
                                     * Keyed by URL so navigating from an
                                     * existing chat to "/chats" forces
                                     * React to remount <Chat/> and wipe any
                                     * lingering compose state (prompt,
                                     * apoyos, references, zone-editor
                                     * modal, etc). Without this, "Nuevo
                                     * chat" felt like nothing was
                                     * happening because the stale state
                                     * from the previous chat carried over.
                                     */}
                                    <ChatRoute />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/chats"
                            element={
                                <AuthGuard>
                                    <ChatRoute />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/library"
                            element={
                                <AuthGuard>
                                    <LibraryPage />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/references"
                            element={
                                <AuthGuard>
                                    <References />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/copy"
                            element={
                                <AuthGuard>
                                    <CopyStudio />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/copy/:id"
                            element={
                                <AuthGuard>
                                    <CopyStudio />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/comunicacion-estandar"
                            element={
                                <AuthGuard>
                                    <CommunicationStandard />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/stats"
                            element={
                                <AuthGuard adminOnly>
                                    <Stats />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/logs"
                            element={
                                <AuthGuard adminOnly>
                                    <Logs />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <AuthGuard adminOnly>
                                    <AdminLayout />
                                </AuthGuard>
                            }
                        >
                            <Route index element={<Navigate to="modelos" replace />} />
                            <Route path="modelos" element={<AdminModels />} />
                            <Route path="configuracion" element={<AdminGeneral />} />
                            <Route path="valoraciones" element={<AdminRatings />} />
                        </Route>
                        <Route
                            path="/users"
                            element={
                                <AuthGuard adminOnly>
                                    <Users />
                                </AuthGuard>
                            }
                        />
                        <Route
                            path="/tutoriales"
                            element={
                                <AuthGuard>
                                    <Tutorials />
                                </AuthGuard>
                            }
                        />
                    </Routes>
                    </ErrorBoundary>
                    <Toaster
                        theme="light"
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                background: "#ffffff",
                                color: "#000000",
                                border: "1px solid #d9d9d9",
                                boxShadow: "0 8px 30px -12px rgba(0,0,0,0.18)",
                            },
                        }}
                    />
                    </BrowserRouter>
                        </ConfirmProvider>
                        </GenerationsProvider>
                    </BrandProvider>
                </PlatformConfigProvider>
            </AuthProvider>
        </div>
    );
}

export default App;

/**
 * Wraps <Chat/> with a React `key` derived from the route param. When the
 * user navigates from `/chats/abc` to `/chats` (or the other way), the key
 * changes and React unmounts the old instance — wiping prompt text,
 * references, apoyos, zone-editor state, pending polls, etc. Without this
 * the "Nuevo chat" button felt dead because the stale state from the
 * previous chat carried over and the UI didn't visibly change.
 */
/**
 * The chat surface lives at BOTH /chats and /chats/:id. We want a full
 * component remount whenever the user clicks "Nuevo chat" so the compose
 * state (prompt, apoyos, references, etc.) resets cleanly. Relying on the
 * URL param alone for the key was brittle because the Chat component uses
 * `window.history.replaceState` after creating a chat lazily, which bypasses
 * react-router and leaves `useParams` stale.
 *
 * Fix: an in-memory seed counter that Chat itself or the "Nuevo chat" button
 * increments via a window event. The key is seed + id so either trigger
 * remounts reliably.
 */
function ChatRoute() {
    const { id } = useParams();
    const [seed, setSeed] = useState(0);
    useEffect(() => {
        const onReset = () => setSeed((s) => s + 1);
        window.addEventListener("kc:new-chat-requested", onReset);
        return () => window.removeEventListener("kc:new-chat-requested", onReset);
    }, []);
    return <Chat key={`${seed}-${id || "new"}`} />;
}
